<script setup lang="ts">
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { useField } from 'vee-validate'
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

type RowId = string | number

const EMPTY_SELECTED_IDS: RowId[] = []

const props = withDefaults(
  defineProps<{
    name: string
    options: Array<any>
    labelAttribute: string
    alreadySelectedOption?: RowId[]
    hasAllOption?: boolean
    /** `string` para enums como alarm_type (ex.: interruption); padrão numérico (ex.: classification id) */
    idMode?: 'number' | 'string'
    /** Campo de filtro na lista (acentos/caixa insensível), alinhado ao `Dropdown.vue` */
    searchable?: boolean
    filterPlaceholder?: string
  }>(),
  { idMode: 'number', alreadySelectedOption: undefined, searchable: false },
)

const emits = defineEmits<{
  (e: 'option-selected', payload: RowId[]): void
  (e: 'all-selected', payload: RowId[]): void
}>()

const dropdownRootRef = ref<HTMLElement | null>(null)
const isDropdownOpen = ref(false)
const selectedOptions = ref<any[]>([])
const filterQuery = ref('')
const searchInputRef = ref<HTMLInputElement | null>(null)

onClickOutside(dropdownRootRef, () => {
  isDropdownOpen.value = false
})
const { value: inputValue, errorMessage } = useField<RowId[]>(props.name)

const filterPlaceholderResolved = computed(() => props.filterPlaceholder ?? t('placeholders.sensors.select_sensor'))

const filteredOptions = computed(() => {
  if (!props.searchable) return props.options
  const labelKey = props.labelAttribute
  return props.options.filter((option) => {
    const label = (option[labelKey] ?? option.name ?? '').toString()
    return matchesSearchAccentInsensitive(label, filterQuery.value)
  })
})

function isSameId(a: RowId, b: RowId): boolean {
  return props.idMode === 'string' ? String(a) === String(b) : a === b
}

function getAlreadySelectedIds(): RowId[] {
  return props.alreadySelectedOption ?? EMPTY_SELECTED_IDS
}

function areSameIdSets(a: RowId[] | undefined, b: RowId[] | undefined): boolean {
  const left = a ?? []
  const right = b ?? []
  if (left.length !== right.length) return false
  return left.every((id, index) => isSameId(id, right[index]))
}

const selectedLabel = computed(() => {
  if (selectedOptions.value.length === props.options.length && props.options.length > 0) {
    return t('globals.dropdowns.all')
  }
  if (selectedOptions.value.length > 0) {
    return `${selectedOptions.value.length} ${t('globals.dropdowns.default.placeholders')}`
  }
  return t('globals.dropdowns.all')
})

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value
  if (isDropdownOpen.value && props.searchable) {
    filterQuery.value = ''
    nextTick(() => searchInputRef.value?.focus())
  }
}

function toggleOption(option: any) {
  const exists = selectedOptions.value.find((o) => isSameId(o.id, option.id))
  if (exists) {
    selectedOptions.value = selectedOptions.value.filter((o) => !isSameId(o.id, option.id))
  } else {
    selectedOptions.value.push(option)
  }
  updateValue()
}

function selectAllOption() {
  selectedOptions.value = []
  inputValue.value = []
  emits('all-selected', [])
  isDropdownOpen.value = false
}

function updateValue() {
  inputValue.value = selectedOptions.value.map((o) => o.id as RowId)
  emits('option-selected', inputValue.value)
}

function isSelected(option: any) {
  return selectedOptions.value.some((o) => isSameId(o.id, option.id))
}

function hydrateFromIds(ids: RowId[]) {
  if (!ids?.length) {
    selectedOptions.value = []
    inputValue.value = []
    return
  }
  selectedOptions.value = props.options.filter((option) => ids.some((id) => isSameId(id, option.id)))
  inputValue.value = selectedOptions.value.map((o) => o.id as RowId)
}

onMounted(() => {
  const raw = getAlreadySelectedIds()
  if (!raw.length) return
  if (typeof raw[0] === 'object') {
    selectedOptions.value = raw as any[]
    updateValue()
  } else {
    hydrateFromIds(raw as RowId[])
  }
})

watch(
  () => props.options,
  () => {
    const ids = getAlreadySelectedIds()
    if (ids.length && selectedOptions.value.length === 0) {
      hydrateFromIds(ids as RowId[])
    }
  },
)

watch(
  () => props.alreadySelectedOption,
  (ids, previousIds) => {
    if (areSameIdSets(ids, previousIds)) return

    if (!ids?.length) {
      if (selectedOptions.value.length === 0 && inputValue.value.length === 0) return
      selectedOptions.value = []
      inputValue.value = []
      return
    }
    if (typeof ids[0] === 'object') return
    hydrateFromIds(ids as RowId[])
  },
)
</script>

<template>
  <div ref="dropdownRootRef" class="relative w-full">
    <button
      type="button"
      @click="toggleDropdown"
      class="w-full border border-zinc-300 bg-white py-3 pl-4 pr-10 text-left transition-colors hover:border-zinc-400 focus:border-primary focus:outline-none">
      <span>{{ selectedLabel }}</span>
      <div class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform">
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="h-4 w-4 text-zinc-400 transition-transform duration-200 ease-in-out"
          :class="isDropdownOpen ? 'rotate-180' : 'rotate-0'">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </button>

    <ul
      v-show="isDropdownOpen"
      class="absolute left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto overscroll-contain bg-white shadow-lg"
      @wheel.stop
    >
      <li
        v-if="hasAllOption"
        class="cursor-pointer bg-green-500 px-3 py-2 text-white transition-colors duration-300 hover:bg-black"
        @click="selectAllOption">
        {{ t('globals.dropdowns.all') }}
      </li>

      <li
        v-if="searchable"
        class="sticky top-0 z-[1] border-b border-zinc-200 bg-white px-2 py-2"
        @mousedown.prevent>
        <input
          ref="searchInputRef"
          v-model="filterQuery"
          type="text"
          class="w-full border border-zinc-300 bg-white py-2 pl-3 pr-3 text-sm transition-colors focus:border-primary focus:outline-none"
          :placeholder="filterPlaceholderResolved"
          @click.stop />
      </li>

      <li
        v-if="searchable && props.options.length > 0 && filteredOptions.length === 0 && filterQuery.trim()"
        class="px-3 py-2 text-sm text-gray">
        {{ t('placeholders.empty_list') }}
      </li>

      <li
        v-for="option in filteredOptions"
        :key="String(option.id)"
        class="flex cursor-pointer items-center justify-between px-3 py-2 font-medium transition-colors duration-300 hover:bg-lightGray">
        <span>{{ option[labelAttribute] }}</span>
        <input type="checkbox" :checked="isSelected(option)" @change="toggleOption(option)" />
      </li>
    </ul>

    <p v-if="errorMessage && !isDropdownOpen" class="mt-1 text-sm text-red-500">
      {{ errorMessage }}
    </p>
  </div>
</template>
